
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AlertPopup, HttpLoader } from 'app/app.config';
import { AskQuestionService } from '../ask-question.service';
import { LlmService } from 'app/services/llm.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AssistBotService } from 'app/components/ai-assist/assist-bot.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TooltipModule } from 'primeng/tooltip';
import { v4 as uuidv4 } from 'uuid'; // Import the v4 generator
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';
import DOMPurify from 'dompurify';


export interface SourceParagraph {

  text: string;

  document_name?: string;

  page_number?: number;

  score?: number;

}


export interface SaveHistoryPayload {

  conversation_id?: number | null;

  history_id?: number;

  sequence_id?: number;

  user_id: number | string;

  question: string;

  answer: string;

  question_type?: string;

  bot_type?: string;

  session_id?: string | null;

  query_type?: 'generic' | 'private' | string;

  source_doc?: string;

  document_ids?: number[] | string[];

  source_paragraphs?: SourceParagraph[];

  group_ids?: number[] | string[];

}

interface Suggestion {
  label: string;
  value: string;
}


@Component({
  selector: 'app-ai',
  imports: [CommonModule, FormsModule, TooltipModule, DialogModule, EditorModule, ButtonModule],
  templateUrl: './ai.component.html',
  styleUrl: './ai.component.scss'
})
export class AiComponent implements OnInit, OnDestroy {

  @ViewChild('scrollContainer')
  scrollContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('contentDiv', { static: true })
  contentDiv!: ElementRef<HTMLDivElement>;

  @Input({ required: true })
  data: any;

  @Input() index!: number;
  @Input() historyItem: any;
  @Input() modelType!: string;
  @Input() documentIds: any[] = [];
  @Input() active = false;
  @Input() aiLoading = false;

  @Output() resubmitQuestion = new EventEmitter<any>();
  @Output() historyCreated = new EventEmitter<any>();

  synth = window.speechSynthesis;

  isVoiceActive = false;

  utterance: SpeechSynthesisUtterance | null = null;

  aiAnswer = '';

  aiBotDate: any;

  typedAnswer = '';

  typingSpeed = 25;

  isLoggedUserId: any;

  shouldScroll = false;

  feedbackOptions = [
    'Helpful',
    'Not Helpful',
    'Incorrect',
    'Incomplete',
    'Other'
  ];

  // private typingInterval: ReturnType<typeof setInterval> | null = null;

  private typingFrame: number | null = null;

  // currentConversationId: number | null = null;

  showTextView: boolean = false;

  searchView: boolean = false;

  cleanedSourceParagraphs: SafeHtml = '';

  isTyping = false;

  showFeedbackPopup = false;

  feedbackType: 'LIKE' | 'UNLIKE' | null = null;;


  typingInterval: number | null = null;

  selectedFeedback = '';

  feedbackComment = '';

  editingIndex: number | null = null;

  editedQuestion = '';

  isEditAnswerPopupVisible = false;
  editAnswerText = '';

  actions = [
    {
      tooltip: 'Copy',
      icon: 'pi pi-clone',
      handler: () => this.onClickCopyBot(this.data.answer)
    },
    {
      tooltip: 'Edit Answer',
      icon: 'pi-pen-to-square',
      handler: () => this.onClickEditAnswer(this.data)
    },
    {
      tooltip: 'Good response',
      icon: 'pi pi-thumbs-up',
      handler: () => this.onFeedback('LIKE')
    },
    {
      tooltip: 'Bad response',
      icon: 'pi pi-thumbs-down',
      handler: () => this.onFeedback('UNLIKE')
    },

    // {
    //   tooltip: 'Read aloud',
    //   icon: 'pi',
    //   toggle: true
    // }
  ];

  suggestions: string[] = [];
  cleanAnswer = '';

  @Output() suggestionSelected = new EventEmitter<string>();

  constructor(
    private httpLoader: HttpLoader,
    private alert: AlertPopup,
    private botService: AskQuestionService,
    private llmService: LlmService,
    private ngZone: NgZone,
    private assistBotService: AssistBotService,
    private sanitizer: DomSanitizer,
  ) {

  }


  ngOnInit(): void {

    this.isLoggedUserId =

      localStorage.getItem(

        'user_id'

      );

    this.syncFromInput();

  }

  getBotImage(): string {
    return this.data?.query_type === 'generic'
      ? 'assets/images/ai-agentic/generic.png'
      : 'assets/images/ai-agentic/private.png';
  }


  formatAnswer(answer: any): string {
    if (typeof answer !== 'string' || !answer.trim()) {
      return '';
    }

    let formatted = answer.trim();

    const escapeHtml = (value: string): string =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const htmlBlocks: string[] = [];

    const protectBlock = (html: string): string => {
      const index = htmlBlocks.push(html) - 1;
      return `\u0000HTMLBLOCK${index}\u0000`;
    };

    // Normalize Markdown fences or table rows wrapped in bold.
    formatted = formatted
      .replace(/^\s*\*\*(```[\w+-]*)\*\*\s*$/gm, '$1')
      .replace(/^\s*\*\*(\|.*\|)\*\*\s*\\?\s*$/gm, '$1')
      .replace(/^\s*\*\*(```)\*\*\s*$/gm, '$1');

    // Parse Markdown tables before escaping the remaining text.
    const lines = formatted.split(/\r?\n/);
    const output: string[] = [];

    const parseRow = (row: string): string[] =>
      row
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split(/(?<!\\)\|/)
        .map(cell => cell.trim().replace(/\\\|/g, '|'));

    const isSeparator = (row: string): boolean => {
      const cells = parseRow(row);

      return (
        cells.length > 0 &&
        cells.every(cell => /^:?-{3,}:?$/.test(cell))
      );
    };

    // Format inline Markdown inside a table cell.
    const formatCell = (value: string): string => {
      let cell = escapeHtml(value);

      cell = cell
        .replace(/`([^`\n]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(
          /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
          '<em>$1</em>'
        );

      return cell;
    };

    for (let i = 0; i < lines.length; i++) {
      const current = lines[i].trim();

      if (
        current.startsWith('|') &&
        i + 1 < lines.length &&
        isSeparator(lines[i + 1].trim())
      ) {
        const headers = parseRow(current);
        const rows: string[][] = [];

        // Skip header and separator.
        i += 2;

        // Collect table rows.
        while (
          i < lines.length &&
          lines[i].trim().startsWith('|')
        ) {
          rows.push(parseRow(lines[i].trim()));
          i++;
        }

        // The outer loop increments i again.
        i--;

        const thead = headers
          .map(cell => `<th>${formatCell(cell)}</th>`)
          .join('');

        const tbody = rows
          .map(row => {
            const cells = headers
              .map((_, index) => {
                return `<td>${formatCell(row[index] ?? '')}</td>`;
              })
              .join('');

            return `<tr>${cells}</tr>`;
          })
          .join('');

        output.push(
          protectBlock(
            `<div class="answer-table-wrapper">` +
            `<table class="answer-table">` +
            `<thead><tr>${thead}</tr></thead>` +
            `<tbody>${tbody}</tbody>` +
            `</table>` +
            `</div>`
          )
        );

        continue;
      }

      output.push(lines[i]);
    }

    formatted = output.join('\n');

    // Convert fenced code blocks.
    formatted = formatted.replace(
      /```([\w+-]*)[ \t]*\r?\n([\s\S]*?)```/g,
      (_match, language: string, code: string) => {
        const safeLanguage = /^[\w+-]*$/.test(language)
          ? language
          : 'text';

        return protectBlock(
          `<pre><code class="language-${safeLanguage}">` +
          `${escapeHtml(code.trimEnd())}` +
          `</code></pre>`
        );
      }
    );

    // Preserve existing HTML while allowing Markdown processing
    // in ordinary text outside HTML blocks.
    const containsHtml =
      /<\/?(?:p|div|table|thead|tbody|tr|td|th|ul|ol|li|h[1-6]|pre|blockquote|span|strong|b|em|i|a|br|hr|code)\b[^>]*>/i.test(
        formatted
      );

    if (containsHtml) {
      // Protect existing HTML tags and their contents from escaping.
      // DOMPurify sanitizes the final HTML before returning it.
      formatted = this.sanitizeAnswer(
        formatted.replace(
          /\*\*([^*\n]+)\*\*/g,
          '<strong>$1</strong>'
        )
      );

      return formatted;
    }

    // Escape only ordinary text; generated blocks stay protected.
    formatted = formatted
      .split(/(\u0000HTMLBLOCK\d+\u0000)/g)
      .map(part =>
        /^\u0000HTMLBLOCK\d+\u0000$/.test(part)
          ? part
          : escapeHtml(part)
      )
      .join('');

    // Horizontal rules.
    formatted = formatted.replace(
      /^[-_*]{3,}[ \t]*$/gm,
      '<hr>'
    );

    // Headings.
    formatted = formatted
      .replace(/^###\s+(.+)$/gm, '<h4>$1</h4>')
      .replace(/^##\s+(.+)$/gm, '<h3>$1</h3>')
      .replace(/^#\s+(.+)$/gm, '<h2>$1</h2>');

    // Inline code.
    formatted = formatted.replace(
      /`([^`\n]+)`/g,
      '<code>$1</code>'
    );

    // Bold and italic.
    formatted = formatted
      .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
      .replace(
        /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
        '<em>$1</em>'
      );

    // Ordered lists.
    formatted = formatted.replace(
      /(?:^|\n)((?:\d+\.\s+.+(?:\n|$))+)/g,
      (_match, listText: string) => {
        const items = listText
          .trim()
          .split('\n')
          .map((line: string) =>
            `<li>${line.replace(/^\d+\.\s+/, '').trim()}</li>`
          )
          .join('');

        return `\n<ol>${items}</ol>\n`;
      }
    );

    // Unordered lists.
    formatted = formatted.replace(
      /(?:^|\n)((?:[-•]\s+.+(?:\n|$))+)/g,
      (_match, listText: string) => {
        const items = listText
          .trim()
          .split('\n')
          .map((line: string) =>
            `<li>${line.replace(/^[-•]\s+/, '').trim()}</li>`
          )
          .join('');

        return `\n<ul>${items}</ul>\n`;
      }
    );

    // Bold labels such as Purpose: or Summary:.
    formatted = formatted.replace(
      /(^|\n)([A-Za-z][^<\n:]{1,100}:)(?=\s)/g,
      '$1<strong>$2</strong>'
    );

    // Restore protected HTML blocks.
    formatted = formatted.replace(
      /\u0000HTMLBLOCK(\d+)\u0000/g,
      (_match, index: string) => htmlBlocks[Number(index)] ?? ''
    );

    // Preserve paragraph spacing and line breaks.
    formatted = formatted
      .split(/\n\s*\n+/)
      .map(block => block.trim())
      .filter(Boolean)
      .map(block => {
        if (
          /^<(?:h[1-6]|ul|ol|div|blockquote|pre|hr)\b/i.test(block)
        ) {
          return block;
        }

        return `<p>${block.replace(/\n/g, '<br>')}</p>`;
      })
      .join('\n');

    return this.sanitizeAnswer(formatted);
  }

  private sanitizeAnswer(html: string): string {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'hr',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'strong', 'b', 'em', 'i', 'u', 's',
        'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tfoot',
        'tr', 'th', 'td', 'caption',
        'div', 'span',
        'blockquote', 'pre', 'code',
        'a', 'sub', 'sup'
      ],
      ALLOWED_ATTR: [
        'href', 'title', 'colspan', 'rowspan',
        'class', 'target', 'scope'
      ],
      ALLOW_DATA_ATTR: false
    });
  }



  private syncFromInput(): void {

    if (!this.data) {

      this.aiAnswer = '';

      this.typedAnswer = '';

      this.aiBotDate = null;

      this.aiLoading = false;

      this.isTyping = false;

      return;

    }

    // const answer =

    //   this.data.answer ?? '';

    // this.aiAnswer = answer;

    // this.typedAnswer = answer;

    // console.log(this.typedAnswer, this.aiLoading, '----------------AI Loading-------------')

    // this.aiBotDate =

    //   this.data.created_at

    //   ??

    //   this.data.history_date

    //   ??

    //   null;

    // this.aiLoading =

    //   !!this.data.isTyping;

    // this.isTyping =

    //   !!this.data.isTyping;

    // this.data.source_paragraphs

    //   ??= [];

    // this.cleanSourceParagraphs();

  }

  onSuggestionClick(suggestion: string): void {

    if (!suggestion?.trim() || this.aiLoading) {
      return;
    }

    const question = suggestion.trim();

    console.log(
      'Selected suggestion:',
      question
    );

    this.resubmitQuestion.emit({
      suggestion: question,
      message: this.data,
      index: this.index
    });
  }


  private parseSuggestions(text: string): string {
    if (!text) {
      this.suggestions = [];
      return '';
    }

    const lines = text.split(/\r?\n/);

    const normalLines: string[] = [];
    const suggestions: string[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (/^SUGGEST\s*:/i.test(trimmedLine)) {
        const suggestion = trimmedLine
          .replace(/^SUGGEST\s*:\s*/i, '')
          .trim();

        if (suggestion) {
          suggestions.push(suggestion);
        }
      } else {
        normalLines.push(line);
      }
    }

    this.suggestions = suggestions;

    return normalLines
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  // onSuggestionClick(suggestion: string): void {
  //   if(!suggestion?.trim() || this.aiLoading) {
  //   return;
  // }

  // this.resubmitQuestion.emit({
  //   question: suggestion.trim(),
  //   data: this.data,
  //   index: this.index
  // });
  //   }

  askAgain(question: string): void {
    // Send directly to AI model
    // this.sendMessageToModel(question);
  }

  get isActive(): boolean {

    return (

      this.data?.history_id

      ===

      this.historyItem?.history_id

    );

  }


  focusContent(): void {

    const element =

      this.contentDiv

        ?.nativeElement;

    if (!element) {

      return;

    }

    element.scrollIntoView({

      behavior: 'smooth',

      block: 'center'

    });

    element.focus({

      preventScroll: true

    });

  }

  ngAfterViewChecked(): void {

    // Scroll only when required

    if (this.shouldScroll) {

      this.focusContent();

      this.shouldScroll = false;

    }

  }


  private prepareQuestions(): string[] {

    const {

      questionArray,

      question

    } = this.data ?? {};

    // Prefer pre-built array

    if (

      Array.isArray(questionArray) &&

      questionArray.length > 0

    ) {

      return questionArray

        .map(

          (q: any) =>

            q?.toString()

              ?.trim()

        )

        .filter(

          (

            q

          ): q is string =>

            !!q

        );

    }

    // Fallback to comma separated string

    if (

      typeof question ===

      'string'

    ) {

      return question

        .split(',')

        .map(

          (q: string) =>

            q.trim()

        )

        .filter(

          Boolean

        );

    }

    return [];

  }



  startTypingEffect(
    text: string
  ): void {

    // Clear previous interval

    if (this.typingInterval) {

      clearInterval(

        this.typingInterval

      );

      this.typingInterval = null;

    }

    text = text ?? '';

    this.typedAnswer = '';

    this.isTyping = true;

    this.aiLoading = false;

    let index = 0;

    const chunkSize = 3;

    const speed = 15;

    this.typingInterval =

      window.setInterval(() => {

        if (

          index >= text.length

        ) {

          clearInterval(

            this.typingInterval!

          );

          this.typingInterval = null;

          this.isTyping = false;

          this.typedAnswer = text;

          return;

        }

        this.typedAnswer +=

          text.slice(

            index,

            index + chunkSize

          );

        index += chunkSize;

      }, speed);

  }

  stopTypingEffect(): void {

    if (

      this.typingInterval

    ) {

      clearInterval(

        this.typingInterval

      );

      this.typingInterval = null;

    }

    this.isTyping = false;

  }


  changeNewLine(
    bot: any
  ): string {

    const text =

      typeof bot ===

        'object'

        ? bot?.answer

        : bot;

    if (!text) {

      return '';

    }

    return text

      .replace(

        /\r?\n/g,

        '<br />'

      )

      .replace(

        /\*\*(.*?)\*\*/g,

        '<strong>$1</strong>'

      );

  }

  onclickFollowOfQuestion(query: any) {
    let questionIdex = 0;

    // this.data.question = query;

    let answerData = [
      {
        "question_id": "",
        "bot_type": "bot",
        "question": query.trim().replace(/"/g, ''),
        "answer": "",
        "source_doc": "",
        "search": true
      }
    ];

    // this.botChatComponent.historyRecord.push(query.trim().replace(/"/g, ''))

    // this.botChatComponent.chatData.push(answerData[0]);

  }

  onClickCopyBot(botResp: any): void {

    const textToCopy = botResp?.text || botResp;

    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy).then(() => {

      this.alert.showAlertMessage({

        success: true,

        message: 'Text copied to clipboard!'

      });

    });

  }

  onFeedback(type: 'LIKE' | 'UNLIKE') {

    this.feedbackType = type;

    if (type === 'LIKE') {

      this.showFeedbackPopup = true;

      // Call API to save positive feedback
      // this.saveFeedback(type);

    } else {

      // Open popup for negative feedback
      this.showFeedbackPopup = true;

    }
  }

  onClickEditAnswer(data: any) {
    this.editAnswerText = this.formatAnswer(data.answer) || '';
    this.isEditAnswerPopupVisible = true;
  }

  onSaveEditedAnswer(): void {
    this.httpLoader.showLoader();

    const historyId = this.data.history_id;

    const payload = {
      history_id: historyId,
      answer: this.editAnswerText
    };

    this.botService.updateHistory(historyId, payload).subscribe({
      next: (response: any) => {
        this.httpLoader.hideLoader();

        // Update UI after successful API response
        this.data.answer = this.editAnswerText;

        console.log(response.message);
        this.alert.showAlertMessage(response);
        this.isEditAnswerPopupVisible = false;
      },
      error: (err: any) => {
        this.httpLoader.hideLoader();

        this.alert.showAlertMessage(
          err?.error?.message || err?.error || 'Failed to update answer'
        );
      }
    });
  }

  onCancelEditAnswer(): void {
    this.isEditAnswerPopupVisible = false;
  }

  saveFeedback(type: 'LIKE' | 'UNLIKE') {
    console.log('Feedback:', type);

    // API call
    // this.aiService.saveFeedback({ type }).subscribe(...)

    this.alert.showAlertMessage({ 'message': 'Feedback sumbitted successfully.' })
  }

  toggleSpeech(content: string) {

    if (this.isVoiceActive) {

      this.stopSpeech();

    } else {

      this.speakBotResponse(content);

    }

  }

  speakBotResponse(content: string) {

    if (!content?.trim()) return;

    this.stopSpeech();

    const cleanText = content

      .replace(/(<([^>]+)>)/gi, '')

      .replace(/&nbsp;/g, ' ');

    this.utterance = new SpeechSynthesisUtterance(cleanText);

    this.utterance.rate = 0.9;

    const voices = this.synth.getVoices();

    this.utterance.voice =

      voices.find(v => v.name === 'Google US English') ||

      voices[0];

    this.ngZone.run(() => {

      this.isVoiceActive = true;

    });

    this.utterance.onend = () => {

      this.ngZone.run(() => {

        this.isVoiceActive = false;

      });

      this.utterance = null;

    };

    this.synth.speak(this.utterance);

  }

  stopSpeech() {

    if (this.synth.speaking) {

      this.synth.cancel();

    }

    this.utterance = null;

    this.isVoiceActive = false;

  }

  toggleTextView() {
    this.showTextView = !this.showTextView;
  }

  cleanSourceParagraphs(): void {

    const raw = this.data?.source_paragraphs;

    this.cleanedSourceParagraphs = '';

    if (!raw) {
      return;
    }

    try {

      const parsed = typeof raw === 'string'
        ? JSON.parse(raw)
        : raw;

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return;
      }

      const seen = new Set<string>();
      const htmlParts: string[] = [];

      for (const item of parsed) {

        if (!item) {
          continue;
        }

        const text = item.text?.trim();

        if (!text || seen.has(text)) {
          continue;
        }

        seen.add(text);

        htmlParts.push(`
        <div class="source-item">
          ${this.escapeHtml(text)}
          <span class="source-doc">
            (Source: ${this.escapeHtml(item.document_name || 'Unknown')})
          </span>
        </div>
      `);
      }

      if (htmlParts.length) {
        this.cleanedSourceParagraphs =
          this.sanitizer.bypassSecurityTrustHtml(
            htmlParts.join('')
          );
      }

    } catch (error) {

      console.error('Invalid source_paragraphs JSON:', error);

      this.cleanedSourceParagraphs = '';
    }
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  currentFeedbackItem: any;

  onActionClick(action: any, items: any): void {

    switch (action.type) {

      case 'good':
        this.currentFeedbackItem = items;
        this.feedbackType = 'LIKE';
        this.feedbackOptions = [
          'Helpful',
          'Accurate',
          'Easy to understand',
          'Complete answer'
        ];
        this.showFeedbackPopup = true;
        break;

      case 'bad':
        this.currentFeedbackItem = items;
        this.feedbackType = 'UNLIKE';
        this.feedbackOptions = [
          'Incorrect',
          'Not relevant',
          'Incomplete',
          'Too long',
          'Unsafe response'
        ];
        this.showFeedbackPopup = true;
        break;

      default:
        action.handler?.(items);
        break;
    }

  }

  closeFeedback() {

    this.showFeedbackPopup = false;

    this.feedbackType = null;

    this.selectedFeedback = '';

    this.feedbackComment = '';
  }

  submitFeedback(items: any): void {
    if (!this.selectedFeedback) {
      return;
    }

    const refId = new Date().valueOf();
    let user_id = localStorage.getItem("user_id");
    let company_id = localStorage.getItem("company_id");

    const payload = {
      history_id: items.history_id,
      user_id: user_id,
      company_id: company_id,
      question: items.question,
      answer: items.answer,
      feedback: this.feedbackType,
      feedback_options: this.selectedFeedback,
      source_doc: items.source_doc,
      comments: this.feedbackComment,
    };
    this.httpLoader.showLoader();
    this.botService.saveFeedback(payload).subscribe({
      next: (res: any) => {
        this.httpLoader.hideLoader();

        this.alert.showAlertMessage({ 'message': 'Feedback submitted successfully' });
        this.closeFeedback();
      },
      error: (err: any) => {
        this.httpLoader.hideLoader();
        this.alert.showAlertMessage({ 'message': 'error' + err })
      }
    })

    // Call API here


  }

  //Edit Question re-submit
  startEdit(index: number, question: string) {

    this.editingIndex = index;

    this.editedQuestion = question;

  }

  cancelEdit() {

    this.editingIndex = null;

    this.editedQuestion = '';

  }

  copyQuestion(question: string): void {
    navigator.clipboard.writeText(question)
      .then(() => {
        console.log('Copied');
        this.alert.showAlertMessage({ 'message': 'Copied to clipboard' })
        // this.toastService.success('Copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        this.alert.showAlertMessage({ 'message': 'Failed to copy:', err })
        // this.toastService.error('Failed to copy');
      });
  }

  submitEditedQuestion(): void {

    const updatedQuestion =

      this.editedQuestion.trim();

    if (!updatedQuestion) {

      return;

    }

    this.editingIndex = null;

    this.editedQuestion = '';

    this.resubmitQuestion.emit(

      updatedQuestion

    );

  }

  handleAction(action: any, items: any): void {

    console.log('Clicked action:', action);

    if (action.toggle) {
      this.toggleSpeech(items.answer);
      return;
    }

    this.onActionClick(action, items);
  }

  stripHtml(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return value
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .trim();
  }

  ngOnDestroy(): void {

    this.stopSpeech();

    if (this.typingInterval) {

      clearInterval(this.typingInterval);

      this.typingInterval = null;

    }

  }


}
